import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import Modal from 'cccisd-modal';
import Tooltip from 'cccisd-tooltip';
import { useQueryClient, useQuery } from 'cccisd-react-query';
import { client } from 'cccisd-apollo';
import { MessageViewer } from 'cccisd-laravel-assignment';
import { Formik, Form, Field, CccisdInput, CccisdWysiwyg, CccisdCheckboxInput } from 'cccisd-formik';
import axios from 'cccisd-axios';
import notification from 'cccisd-notification';
import { getFormattedDate } from 'js/helpers';
import { useOptimisticInvite } from './useOptimisticInvite';
import getPlanMessagesQuery from '../getPlanMessages.graphql';
import style from '../style.css';
import IconMail from 'cccisd-icons/paperplane';

const Boilerplate = window.cccisd.boilerplate;
const Fortress = window.cccisd.fortress;

const InviteModal = ({ row, assessmentLink, setForceReloadQueries }) => {
    const studentPawnFields = row.assessment.selectedByPawn.fields;
    const studentInitials = `${studentPawnFields.firstName?.charAt(0)}${studentPawnFields.lastName?.charAt(0)}` || '';
    const queryClient = useQueryClient();
    const { isOptimisticallyUpdated, markAsSent } = useOptimisticInvite(row);
    const reporterTypeLower = useMemo(() => row.reporterType?.toLowerCase(), [row.reporterType]);

    const actingProvider = Fortress.user?.acting?.user || {};
    let replyAddress = actingProvider.email || '';

    useQuery({
        queryKey: ['getPlanMessages', row.assessment?.assignmentPlanId],
        queryFn: async () => {
            const res = await client.query({
                query: getPlanMessagesQuery,
                variables: { planId: [row.assessment?.assignmentPlanId] },
                fetchPolicy: 'network-only',
            });

            return res.data.flows.assignmentPlan;
        },
        enabled: !!row.assessment?.assignmentPlanId,
    });

    const MessageValidate = values => {
        const errors = {};

        if (!row.name && !values.name) {
            errors.name = 'Name is required';
        }

        if (!values.recipient) {
            errors.recipient = 'Email Address is required';
        }
        if (!values.subject || !values.subject.trim()) {
            errors.subject = 'Email subject is required';
        }
        return errors;
    };

    const MessageSubmit = async (values, closeModal) => {
        try {
            let fromName = '';
            if (actingProvider.full_name) {
                fromName = actingProvider.full_name;
            } else if (actingProvider.first_name && actingProvider.last_name) {
                fromName = `${actingProvider.first_name} ${actingProvider.last_name}`;
            } else if (actingProvider.first_name) {
                fromName = actingProvider.first_name;
            }

            // Update pawn info if we did not have name or email
            if (!row.name || !row.email) {
                // Students should always have a name, but may not have an email
                let updatedPawnValues = {
                    email: values.recipient || '',
                };

                try {
                    if (row.reporterType === 'Student') {
                        await axios.put(
                            Boilerplate.route('api.nexus.pawn.update', {
                                pawn: row.pawnId,
                            }),
                            {
                                pawnHash: row.pawnHash,
                                ...updatedPawnValues,
                            }
                        );
                    } else {
                        // Contacts may not have a name because placeholder contact pawns are created in the backend when a student is created
                        updatedPawnValues = {
                            contactName: values.name,
                            contactEmail: values.recipient,
                        };
                        await axios.put(
                            Boilerplate.route('api.nexus.pawn.update', {
                                pawn: row.pawnId,
                            }),
                            {
                                pawnHash: row.pawnHash,
                                ...updatedPawnValues,
                            }
                        );
                    }
                } catch (e) {
                    console.error(e);
                    notification({
                        message: 'An error occured updating contact information',
                        type: 'danger',
                    });
                }
            }

            const response = await axios.post(Boilerplate.route('api.assessment.sendNotification'), {
                recipient: values.recipient,
                sendMethod: 'email',
                messageInfo: {
                    subject: values.subject,
                    body: values.body,
                    actionUrl: assessmentLink,
                    actionText: values.actionText,
                    fromName: fromName ? fromName + ' via SHARE' : '',
                    replyName: fromName,
                    replyAddress,
                },
                messageLogData: {
                    pawn_id: row.pawnId,
                    deployment_id: row.deploymentId,
                    action: row.numberOfTimesInvited > 0 ? 'assessment_plan_remind' : 'assessment_plan_invite',
                },
                cc: values.cc ? [replyAddress] : [],
            });
            if (response && response.status === 200) {
                notification({ message: 'Email has been sent.', type: 'success' });

                markAsSent();
                closeModal();
                await queryClient.invalidateQueries(['getPlanMessages', row.assessment?.assignmentPlanId]);

                setTimeout(() => {
                    queryClient.invalidateQueries(['getPlanMessages', row.assessment?.assignmentPlanId]);
                }, 1500);

                setTimeout(() => {
                    queryClient.invalidateQueries(['getPlanMessages', row.assessment?.assignmentPlanId]);
                }, 3000);
            }
        } catch (e) {
            notification({ message: 'An error occured sending email!', type: 'danger' });
            console.error(e);
        }
    };

    const getSubject = () => {
        if (reporterTypeLower === 'student') {
            return `[${row.school}] Surveys from ${Fortress.user.acting.user.full_name} for you to take`;
        }

        if (reporterTypeLower === 'caregiver') {
            return `[${row.school}] Assessments for you to take regarding your student`;
        }

        return `[${row.school}] Request from ${Fortress.user.acting.user.full_name} to complete assessments regarding your student`;
    };

    const getBody = () => {
        if (reporterTypeLower === 'student') {
            return `<p>Hello,</p><p>You have a new survey from ${Fortress.user.acting.user.full_name} at ${row.school}. Click the button below to take it. All responses are confidential.</p>`;
        }

        if (reporterTypeLower === 'caregiver') {
            return `<p>Hello${row.name ? ' ' + row.name : ''},</p><p>${Fortress.user.acting.user.full_name} at ${
                row.school
            } is requesting information about  ${studentInitials}. Please use the button below to complete these assessments. All responses are confidential.</p>`;
        }

        return `<p>Hello${row.name ? ' ' + row.name : ''},</p><p>You have new assessments from ${
            Fortress.user.acting.user.full_name
        } at ${
            row.school
        }. Please use the button below to complete assessments about ${studentInitials}. All responses are confidential.</p>`;
    };

    return (
        <Modal
            title={`Send Invitation for ${row.name ? row.name + ' -' : ''} ${reporterTypeLower} assessment`}
            trigger={
                <>
                    {row.numberOfTimesInvited > 0 || isOptimisticallyUpdated ? (
                        <Tooltip
                            title={
                                isOptimisticallyUpdated
                                    ? 'Email sent!'
                                    : `${
                                          row.numberOfTimesInvited
                                      } invitations previously sent, most recently on ${getFormattedDate(
                                          row.messages[0]?.sentAt
                                      )}`
                            }
                        >
                            <button className="btn btn-warning btn-sm" type="button">
                                <IconMail /> Resend
                            </button>
                        </Tooltip>
                    ) : (
                        <Tooltip
                            title={`Invite ${
                                row.name ? row.name : `this ${reporterTypeLower}`
                            } to complete assessment(s).`}
                        >
                            <button className="btn btn-secondary btn-sm" type="button">
                                <IconMail /> Invite
                            </button>
                        </Tooltip>
                    )}
                </>
            }
            size="60vw"
            render={({ closeModal }) => {
                return (
                    <>
                        <div>
                            <Formik
                                initialValues={{
                                    recipient: row.email || '',
                                    subject: getSubject(),
                                    body: getBody(),
                                    actionText: reporterTypeLower === 'student' ? 'Go to Survey' : 'Go to Assessments',
                                }}
                                validate={MessageValidate}
                                onSubmit={values => MessageSubmit(values, closeModal)}
                                render={form => (
                                    <div>
                                        {row.numberOfTimesInvited > 0 && (
                                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                                                <div
                                                    style={{
                                                        width: '70%',
                                                        textAlign: 'center',
                                                        borderRadius: '10px',
                                                        padding: '3px',
                                                    }}
                                                    className={isOptimisticallyUpdated ? 'bg-success' : 'bg-warning'}
                                                >
                                                    {`${
                                                        row.numberOfTimesInvited
                                                    } invitations previously sent, most recently on ${getFormattedDate(
                                                        row.messages[0]?.sentAt
                                                    )}`}
                                                </div>
                                            </div>
                                        )}

                                        <Form>
                                            <div className={style.invite_form_body}>
                                                <div style={{ width: '70%' }}>
                                                    {!row.name && (
                                                        <Field
                                                            name="name"
                                                            component={CccisdInput}
                                                            label="Recipient Name"
                                                            required
                                                        />
                                                    )}
                                                    <Field
                                                        name="recipient"
                                                        component={CccisdInput}
                                                        label="Recipient Email Address"
                                                        type="email"
                                                        required
                                                        disabled={row.email}
                                                    />
                                                    <Field
                                                        name="cc"
                                                        component={CccisdCheckboxInput}
                                                        label={`Send a copy to me (${replyAddress})`}
                                                    />
                                                    <Field
                                                        name="subject"
                                                        component={CccisdInput}
                                                        label="Email Subject"
                                                        wrapperClassName={'mb-3 ' + style.required}
                                                    />
                                                    <Field
                                                        name="body"
                                                        component={CccisdWysiwyg}
                                                        settings={{
                                                            toolbar: ['bold italic underline'],
                                                            menubar: false,
                                                        }}
                                                        label={
                                                            reporterTypeLower === 'caregiver'
                                                                ? 'Invitation Body'
                                                                : 'Email Contents'
                                                        }
                                                        type="textarea"
                                                        required
                                                    />
                                                </div>
                                            </div>
                                            <div className={style.invite_modal_buttons}>
                                                <Modal
                                                    trigger={
                                                        <button className="btn btn-secondary btn-lg" type="submit">
                                                            Preview
                                                        </button>
                                                    }
                                                    size="70vw"
                                                    contentStyle={{ marginTop: '25px' }}
                                                    render={() => {
                                                        return (
                                                            <div>
                                                                <MessageViewer
                                                                    deployment={{ hash: '' }}
                                                                    messageObject={{
                                                                        condition: 'template',
                                                                        messageMethod: 'email',
                                                                        category: 'preview',
                                                                        showInfoArea: false,

                                                                        recipients: [form.values.recipient],

                                                                        settings: {
                                                                            recipientsType: 'Preview',
                                                                            label: 'Preview',
                                                                        },
                                                                        messageInfo: {
                                                                            subject: form.values.subject,
                                                                            body: form.values.body,
                                                                            actionUrl: assessmentLink,
                                                                            actionText:
                                                                                reporterTypeLower === 'student'
                                                                                    ? 'Go to Survey'
                                                                                    : 'Go to Assessments',
                                                                        },
                                                                    }}
                                                                />
                                                            </div>
                                                        );
                                                    }}
                                                />
                                                <button className="btn btn-primary btn-lg" type="submit">
                                                    Send
                                                </button>
                                            </div>
                                        </Form>
                                    </div>
                                )}
                            />
                        </div>
                    </>
                );
            }}
        />
    );
};

InviteModal.propTypes = {
    row: PropTypes.object.isRequired,
    assessmentLink: PropTypes.string,
    setForceReloadQueries: PropTypes.func,
};

export default InviteModal;
